import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { GridRenderCellParams } from '@mui/x-data-grid-pro';
import DataGridLinkComponent from '../DataGridLinkComponent';
import { useDispatch, useSelector } from 'react-redux';
import {
	addSelectedRecord,
	removeSelectedRecord,
	selectIsRecordSelected,
} from '../../../redux/slices/selectedGridRecordsSlice';
import type { RootState } from '../../../redux/store';
import type { DataGridType } from '../DataGridTypes';
import { INVOICES } from '../../../constants/messages';
import { assetProps, contractProps, invoiceProps } from '../columnDefinitions';

interface MobileGridCellParams extends GridRenderCellParams {
	showCheckbox?: boolean;
	dataGridType?: DataGridType;
}

export const ListViewCell = (params: MobileGridCellParams) => {
	const dispatch = useDispatch();

	function renderCell(field: string, value: any) {
		const clickableFields = [
			String(assetProps.assetNumber),
			String(contractProps.contractNumber),
			String(invoiceProps.invoiceNumber),
		];
		const dontShowLink =
			(params?.dataGridType?.title === INVOICES &&
				field === String(contractProps.contractNumber)) ||
			!clickableFields.includes(field);

		const setRecordFunc = params?.dataGridType?.setSelectedRecordsAction;

		if (!dontShowLink) {
			return (
				<DataGridLinkComponent
					onClick={() => {
						if (setRecordFunc) {
							dispatch(setRecordFunc(params.row[field]));
						}
					}}
				>
					{params.row[field]}
				</DataGridLinkComponent>
			);
		}
		return value?.toString() || '-';
	}

	const row = params.row;

	// Determine if the row is checked based on its ID
	const checked = useSelector((state: RootState) => selectIsRecordSelected(state, row.id));

	const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const isChecked = event.target.checked;

		if (isChecked) {
			// Add entityType property to the row object
			dispatch(addSelectedRecord(row));
		} else {
			dispatch(removeSelectedRecord(row.id));
		}
	};

	// Only show fields that are present in dataGridType.mobileColumns (by field key)
	const mobileFields = Array.isArray(params?.dataGridType?.mobileColumns)
		? params.dataGridType.mobileColumns.map((col) => col.field)
		: [];

	const showCheckbox = params?.dataGridType?.showCheckBoxOnMobile ?? true;
	return (
		<Stack
			data-testid="mobile-list-view-cell"
			sx={{
				alignItems: 'center',
				gap: 2,
				display: 'flex',
				flexDirection: 'row',
				padding: 2,
				mt: 4,
				mb: 2,
			}}
		>
			{showCheckbox && (
				<Checkbox
					sx={{ p: 0.5, alignSelf: 'flex-start' }}
					onChange={handleCheckboxChange}
					checked={checked}
				/>
			)}
			<Stack sx={{ flex: 1 }}>
				{Object.entries(row)
					.filter(([key]) => key !== 'id' && mobileFields.includes(key))
					.map(([key, value]) => (
						<Box key={key} sx={{ display: 'flex', mb: 0.5 }}>
							<Typography variant="body1" sx={{ ml: 1 }}>
								{renderCell(key, value)}
							</Typography>
						</Box>
					))}
			</Stack>
		</Stack>
	);
};
